import { cn } from '@workspace/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ComponentType, type InputHTMLAttributes, type ReactNode } from 'react';
import { RenderedContent } from '../../utils';

const radioVariants = cva(
  [
    'group bg-radio-bg flex items-center relative w-full rounded-md px-4 text-center border',
    'transition-all duration-200 ease-in-out cursor-pointer',
    'has-[:checked]:bg-radio-checked-bg has-[:checked]:text-radio-checked-text has-[:checked]:border-radio-checked-border',
    'disabled:opacity-75 disabled:cursor-not-allowed',
  ],
  {
    variants: {
      type: {
        small: 'py-2.5 gap-2',
        large: 'flex-col py-4 sm:py-8 gap-2 md:gap-4',
      },
      variant: {
        default: ['border-radio-border', 'hover:border-radio-hover-border hover:bg-radio-hover-bg hover:text-radio-hover-text'],
        error: ['border-radio-error-border', 'hover:border-radio-error-border hover:bg-radio-error-bg'],
      },
    },
    defaultVariants: {
      type: 'large',
      variant: 'default',
    },
  },
);

const radioIndicatorVariants = cva(
  [
    'h-5 w-5 rounded-full border flex items-center justify-center text-radio-border',
    'peer-checked:bg-radio-checked-indicator peer-checked:border-radio-checked-indicator',
    'transition-all duration-200',
  ],
  {
    variants: {
      variant: {
        default: [
          'border-radio-border',
          'group-hover:bg-radio-hover-indicator group-hover:border-radio-hover-indicator group-hover:text-radio-checked-text group-hover:peer-checked:text-radio-border',
        ],
        error: ['border-radio-error-border', 'group-hover:bg-radio-error-indicator group-hover:border-radio-error-indicator'],
      },
      disabled: {
        true: 'opacity-75 group-hover:bg-transparent group-hover:border-radio-border',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      disabled: false,
    },
  },
);

const radioLabelVariants = cva('', {
  variants: {
    type: {
      small: 'text-sm sm:text-sm',
      large: 'text-sm sm:text-base',
    },
  },
  defaultVariants: {
    type: 'large',
  },
});

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>, VariantProps<typeof radioVariants> {
  children?: ReactNode;
  className?: string;
  label?: RenderedContent;
  description?: RenderedContent;
  error?: boolean;
  type?: 'small' | 'large';
  icon?: ComponentType<any>;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ className, label, type = 'large', variant, children, error, disabled, icon, description, ...props }, ref) => {
    const computedVariant = error ? 'error' : variant || 'default';

    const IconComponent = icon as ComponentType<any>;

    return (
      <label
        className={cn(
          radioVariants({ type, variant: computedVariant }),
          // why: le label ne peut pas être :disabled, l'état désactivé est porté par l'input
          disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
          className,
        )}
        aria-disabled={disabled || undefined}
      >
        <input
          className="opacity-0 absolute inset-0 w-full h-full cursor-pointer peer"
          ref={ref}
          type="radio"
          disabled={disabled}
          {...props}
        />

        <span className={radioIndicatorVariants({ variant: computedVariant, disabled: !!disabled })}>
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M5 13l4 4L19 7"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
        </span>

        {IconComponent && <IconComponent className={cn(type === 'large' ? 'w-10 h-10 sm:w-12 sm:h-12' : 'w-5 h-5', 'fill-current')} />}
        {children}

          <div className="flex flex-col items-center space-y-1">
            {label && <span className={radioLabelVariants({ type })}>{label}</span>}
              {description && (<span className="text-xs mt-0">{description}</span>)}
          </div>
      </label>
    );
  },
);

Radio.displayName = 'Radio';
